import { Box, Typography } from "@mui/material"
import { CheckCircleFilledRounded } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"

import { AddContactLocationRolesFormFields } from "@/components/Contact/AddContactLocationRolesFormFields"
import { BLANK_CONTACT_LOCATION_ROLES } from "@/constants/contacts"
import { useGetLocations } from "@/hooks/useGetLocations"
import type { ContactAddFormValues, ContactPhoneFormValues } from "@/schemas/contactFormSchema"
import { formatLocationAddress } from "@/utils/utils"

export interface AddLocationRolesFormProps {
  companyId: string
}

export const AddContactLocationRolesForm: FC<AddLocationRolesFormProps> = ({ companyId }) => {
  const methods = useFormContext<ContactAddFormValues>()
  const { control, getValues, watch } = methods
  const { locations } = useGetLocations(companyId)
  const { fields, append } = useFieldArray({
    control,
    name: "locationRoles",
  })
  const formValues = getValues()
  const officeLocation = locations.find((location) => location.locationId === formValues.physicalLocationId)
  const locationRoles = watch("locationRoles")

  const getPhoneDisplayValue = (phone: ContactPhoneFormValues): string => {
    if (!phone.number) return "Not added"

    return phone.isPreferred ? `${phone.number} Preferred` : phone.number
  }

  const handleAddLocationRoles = (): void => {
    append(BLANK_CONTACT_LOCATION_ROLES)
  }

  return (
    <form className='space-y-6'>
      <Box className='space-y-4'>
        <Box className='mb-2' display='flex' alignItems='center'>
          <Typography
            variant='body1'
            fontWeight={700}
            className='leading-6 tracking-[0.15px] text-gray-700'
            display='flex'
            alignItems='center'
          >
            1. General Information
            <CheckCircleFilledRounded className='ml-1 text-success-500' />
          </Typography>
        </Box>
        {/* TODO: AK-2842 */}
        <Box className='px-10'>
          <p className='py-0 text-sm text-gray-900'>First Name: {formValues.firstName}</p>
          <p className='py-0 text-sm text-gray-900'>Last Name: {formValues.lastName}</p>
          {formValues.companyDepartment && (
            <p className='py-0 text-sm text-gray-900'>Company Department: {formValues.companyDepartment}</p>
          )}
          {formValues.title && <p className='py-0 text-sm text-gray-900'>Title: {formValues.title}</p>}
          <p className='py-0 text-sm text-gray-900'>Office Location: {formatLocationAddress(officeLocation)}</p>
        </Box>
      </Box>
      <Box className='space-y-4'>
        <Box className='mb-2' display='flex' alignItems='center'>
          <Typography
            variant='body1'
            fontWeight={700}
            className='leading-6 tracking-[0.15px] text-gray-700'
            display='flex'
            alignItems='center'
          >
            2. Contact Methods
            <CheckCircleFilledRounded className='ml-1 text-success-500' />
          </Typography>
        </Box>
        <Box className='px-10'>
          {(formValues.phones ?? []).map((phone, index) => (
            <Box key={index} className='space-y-2'>
              <p className='py-0 text-sm text-gray-900'>
                Phone {`(${phone.type ?? "Text"})`}: {getPhoneDisplayValue(phone)}
              </p>
            </Box>
          ))}
          <p className='py-0 text-sm text-gray-900'>Email: {formValues.email || "Not added"}</p>
        </Box>
      </Box>
      <Typography variant='body1' fontWeight={700} className='leading-6 tracking-[0.15px] text-gray-700'>
        3. Locations Roles
      </Typography>
      <AddContactLocationRolesFormFields<ContactAddFormValues>
        fieldArrayName={"locationRoles"}
        fields={fields}
        contactName={`${formValues.firstName} ${formValues.lastName}`}
        methods={methods}
        locations={locations}
        control={control}
        currentLocationRoles={[]}
        newLocationRoles={locationRoles}
        onAdd={handleAddLocationRoles}
      />
    </form>
  )
}
